import React from 'react';
import { Loader2, Pill } from 'lucide-react';
import DrawerShell from '../DrawerShell';
import { formatDateIST } from '../../utils/date';
import {
  ClinicalMilestoneTimeline,
  MilestoneEmpty,
  MilestoneItem,
} from './ClinicalMilestoneTimeline';
import { usePatientClinicalHistory } from './patientHistory.api';

const ACCENT = '#071B34';

/** Milestone timeline of all medicines prescribed across visits. */
export default function MedicineHistoryDrawer({ open, onClose, patientId, patientName }) {
  const { visits, loading } = usePatientClinicalHistory(patientId, open);
  const withRx = visits.filter((v) => (v.prescription || []).length > 0);

  return (
    <DrawerShell
      open={open}
      onClose={onClose}
      title="Medicine History"
      subtitle={patientName ? `${patientName} · all prescriptions by visit` : 'All prescriptions by visit'}
      eyebrow="IIT Hyderabad Hospital"
      accent={ACCENT}
      icon={<Pill className="h-5 w-5 text-white" />}
      width="lg"
      testId="medicine-history-drawer"
    >
      <div className="p-5">
        {loading ? (
          <div className="py-16 text-center text-[#9AADBE]">
            <Loader2 className="mx-auto h-6 w-6 animate-spin" />
            <p className="mt-2 text-sm">Loading medicines…</p>
          </div>
        ) : withRx.length === 0 ? (
          <MilestoneEmpty icon={Pill} text="No medicines on record yet" />
        ) : (
          <ClinicalMilestoneTimeline accent={ACCENT} testId="medicine-milestone-timeline">
            {withRx.map((v, i) => (
              <MilestoneItem key={v.consultation_id || i} accent={ACCENT} testId={`med-visit-${i}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-[#071B34]">
                    {v.date ? formatDateIST(v.date) : '—'}
                  </p>
                  <span className="rounded border border-[#D5DEE8] bg-[#E8EEF5] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#5A6B7C]">
                    {(v.prescription || []).length} medicine{(v.prescription || []).length === 1 ? '' : 's'}
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-[#5A6B7C]">Dr. {v.doctor_name || '—'}</p>
                <ul className="mt-2 space-y-1.5">
                  {(v.prescription || []).map((m, mi) => (
                    <li
                      key={`${m.name}-${mi}`}
                      className="rounded-lg border border-[#D5DEE8] bg-[#F8FAFC] px-2.5 py-2"
                      data-testid={`med-item-${i}-${mi}`}
                    >
                      <p className="text-sm font-semibold text-[#071B34]">{m.name}</p>
                      <p className="mt-0.5 text-[11px] text-[#5A6B7C]">
                        {[m.dose, m.timing, m.intake, m.duration].filter(Boolean).join(' · ') || '—'}
                      </p>
                      {m.notes ? <p className="mt-0.5 text-[10px] italic text-[#9AADBE]">{m.notes}</p> : null}
                    </li>
                  ))}
                </ul>
              </MilestoneItem>
            ))}
          </ClinicalMilestoneTimeline>
        )}
      </div>
    </DrawerShell>
  );
}
